import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Collaborator } from "./types";

interface CollaboratorListProps {
  collaborators: Collaborator[];
  ownerEmail: string;
  currentUserEmail: string;
  onRequestRemove?: (collaborator: Collaborator) => void;
  canRemove?: boolean;
}

function getInitial(email: string): string {
  return email.charAt(0).toUpperCase();
}

interface RowProps {
  email: string;
  isCurrentUser: boolean;
  isOwner: boolean;
  showRemove: boolean;
  onRemove?: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function Row({
  email,
  isCurrentUser,
  isOwner,
  showRemove,
  onRemove,
  t,
}: RowProps) {
  const avatarClass = isOwner
    ? "text-xs bg-primary-25 text-primary-500"
    : "text-xs bg-secondary-100 text-secondary-600";
  const containerClass = isOwner
    ? "flex items-center gap-3 rounded-lg px-2 py-1.5"
    : "flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-secondary-50 transition-colors group";

  return (
    <div className={containerClass}>
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback className={avatarClass}>
          {getInitial(email)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="truncate text-sm text-foreground">{email}</span>
        {isCurrentUser && (
          <Badge
            variant="role"
            className="text-[0.65rem] px-1.5 py-0 h-5 shrink-0"
          >
            {t("modal.youLabel")}
          </Badge>
        )}
      </div>
      {isOwner && (
        <span className="text-xs text-muted-foreground shrink-0">
          {t("modal.ownerLabel")}
        </span>
      )}
      {showRemove && onRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-secondary-500 hover:text-destructive"
          onClick={onRemove}
          data-testid={`remove-collaborator-${email}`}
          aria-label={t("modal.removeAriaLabel", { email })}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export function CollaboratorList({
  collaborators,
  ownerEmail,
  currentUserEmail,
  onRequestRemove,
  canRemove = false,
}: CollaboratorListProps) {
  const { t } = useTranslation("sharing");
  const normalizedCurrent = currentUserEmail.toLowerCase();
  const normalizedOwner = ownerEmail.toLowerCase();

  return (
    <div className="flex flex-col gap-1">
      {collaborators.map((collab) => (
        <Row
          key={collab.email}
          email={collab.email}
          isCurrentUser={collab.email.toLowerCase() === normalizedCurrent}
          isOwner={false}
          showRemove={canRemove && !!onRequestRemove}
          onRemove={onRequestRemove ? () => onRequestRemove(collab) : undefined}
          t={t}
        />
      ))}

      {/* Owner row -- always last */}
      <Row
        email={ownerEmail}
        isCurrentUser={normalizedOwner === normalizedCurrent}
        isOwner
        showRemove={false}
        t={t}
      />
    </div>
  );
}
